/**
 * The page-top budget banner (Adam, 6 Oct walkthrough): what a team member
 * is told on every page about their team's AI budget, and whether it shows.
 *
 * Two states. 'warn' is 90% or more of the allocation used, with interactive
 * work still allowed. 'used' is the budget used for interactive work, or a
 * team with no budget now (none ever, its budget year ended, or its year not
 * started yet): the refusal sentence, as everywhere else. Batch work stopping
 * alone does not raise the banner; Your AI access says that.
 *
 * It does not show for a caller with no budget applying: /me carries no
 * `budget` for a Manager, a service account, an agent, a switched-off
 * customer, or a person with no team. The words come from here alone, so the
 * billing simulator can quote them.
 */
import type { AIRealmAdmin, MyAIBudget } from '../types/gotypes'
import { noBudgetReason, refusalSentence, teamManager } from './aiAccess'

/** The share of the allocation used at which the warning starts. */
export const BUDGET_WARN_PCT = 90

export type BudgetBannerKey = 'warn' | 'used'

export interface BudgetBanner {
  key: BudgetBannerKey
  severity: 'warning' | 'error'
  text: string
}

/** "Your team has used 92% of its AI budget. Please contact your team manager to discuss a change." Names the team's managers when given, else the Realm admins. */
export const budgetWarnSentence = (pct: number, admins: AIRealmAdmin[] | undefined | null, managers?: AIRealmAdmin[] | null): string =>
  `Your team has used ${Math.floor(pct)}% of its AI budget. Please contact ${teamManager(admins, managers)} to discuss a change.`

export const budgetBannerFor = (
  access: { enforced: boolean; budget?: MyAIBudget; admins?: AIRealmAdmin[] | null; teamManagers?: AIRealmAdmin[] | null },
): BudgetBanner | null => {
  const { enforced, budget, admins, teamManagers: team_managers } = access
  if (!enforced || !budget) return null
  if (budget.no_budget) {
    return {
      key: 'used', severity: 'error',
      text: refusalSentence({ reason: noBudgetReason(budget.state), admins, team_managers, starts_at: budget.starts_at, ends_at: budget.ends_at }),
    }
  }
  if (budget.exhausted.interactive) return { key: 'used', severity: 'error', text: refusalSentence({ reason: 'budget_exhausted', admins, team_managers }) }
  if ((budget.pct ?? 0) >= BUDGET_WARN_PCT) return { key: 'warn', severity: 'warning', text: budgetWarnSentence(budget.pct, admins, team_managers) }
  return null
}

const storageKey = (customer: string, key: BudgetBannerKey) => `ai-budget-banner:${customer.toLowerCase()}:${key}`

/** Dismissed for this browser session? One key per customer and state, so a dismissed warning does not hide "used". */
export const isBannerDismissed = (customer: string, key: BudgetBannerKey): boolean => {
  try {
    return window.sessionStorage.getItem(storageKey(customer, key)) === '1'
  } catch {
    return false
  }
}

export const dismissBanner = (customer: string, key: BudgetBannerKey): void => {
  try {
    window.sessionStorage.setItem(storageKey(customer, key), '1')
  } catch {
    // Storage blocked: the banner closes for this render only.
  }
}
